import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  CampaignHeader,
  CampaignLayout,
  CampaignMainContent,
} from '@/layouts/campaign.layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { WithTooltip } from '@/components/with-tooltip';
import { Loader2 } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { useShopifySetupDeeplink } from '@/hooks/use-shopify-setup-deeplink';

export default function WebsiteBannerSetupPage() {
  const { LL } = useI18nContext();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [deeplinkOpened, setDeeplinkOpened] = useState(false);
  const [addedAndSavedChecked, setAddedAndSavedChecked] = useState(false);

  const deeplinkQuery = useShopifySetupDeeplink();
  const setupDeeplink = deeplinkQuery.data ?? null;
  const isLoading = deeplinkQuery.isLoading;
  const canContinue = deeplinkOpened && addedAndSavedChecked;

  const handleOpenDeeplink = () => {
    if (setupDeeplink) {
      window.open(setupDeeplink, '_blank', 'noopener,noreferrer');
      setDeeplinkOpened(true);
    }
  };

  const handleContinue = () => {
    navigate(
      `/${tenantSlug}/customization/website-banner?shopifySetupComplete=true`,
      { replace: true }
    );
  };

  return (
    <CampaignLayout>
      <CampaignHeader
        backTo="../.."
        title={LL.websiteBannerPage.setup.title()}
      />
      <CampaignMainContent>
        <div className="flex min-h-[60vh] items-center justify-center p-6 max-md:min-h-0 max-md:p-0">
          <Card className="w-full max-w-lg max-md:rounded-mobile-card">
            <CardHeader>
              <CardTitle className="text-lg">
                {LL.websiteBannerPage.setup.title()}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground">
                {LL.websiteBannerPage.setup.description()}
              </p>
              <p className="text-sm text-muted-foreground">
                {LL.websiteBannerPage.setup.descriptionSaveHint()}
              </p>
              <p className="text-sm text-muted-foreground">
                {LL.websiteBannerPage.setup.descriptionNoDisplayYet()}
              </p>
              {isLoading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>{LL.websiteBannerPage.setup.creating()}</span>
                </div>
              ) : (
                <>
                  <Button
                    className="w-full max-md:min-h-touch"
                    onClick={handleOpenDeeplink}
                    disabled={!setupDeeplink}
                    variant={deeplinkOpened ? 'secondary' : 'default'}
                  >
                    {LL.websiteBannerPage.setup.openShopify()}
                  </Button>
                  <WithTooltip
                    tooltip={LL.websiteBannerPage.setup.checkboxTooltip()}
                    disabled={deeplinkOpened}
                    className="max-w-[320px]"
                  >
                    <div className="flex items-center gap-2 space-y-0 rounded-md border border-transparent p-0 max-md:min-h-touch">
                      <Checkbox
                        id="added-and-saved"
                        checked={addedAndSavedChecked}
                        onCheckedChange={(checked) =>
                          setAddedAndSavedChecked(checked === true)
                        }
                        disabled={!deeplinkOpened}
                      />
                      <Label
                        htmlFor="added-and-saved"
                        className="cursor-pointer text-sm font-normal leading-relaxed text-muted-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {LL.websiteBannerPage.setup.checkboxLabel()}
                      </Label>
                    </div>
                  </WithTooltip>
                  {/* Tooltips need hover: on the phone the same help is text. */}
                  {!deeplinkOpened && (
                    <p className="text-xs text-muted-foreground md:hidden">
                      {LL.websiteBannerPage.setup.checkboxTooltip()}
                    </p>
                  )}
                  <WithTooltip
                    tooltip={LL.websiteBannerPage.setup.continueTooltip()}
                    className="block w-full"
                    disabled={canContinue}
                  >
                    <span className="block w-full">
                      <Button
                        className="w-full max-md:min-h-touch"
                        disabled={!canContinue}
                        onClick={handleContinue}
                      >
                        {LL.websiteBannerPage.setup.continue()}
                      </Button>
                    </span>
                  </WithTooltip>
                  {!canContinue && (
                    <p className="text-xs text-muted-foreground md:hidden">
                      {LL.websiteBannerPage.setup.continueTooltip()}
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </CampaignMainContent>
    </CampaignLayout>
  );
}
